import React, { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../firebase";
import "../css/CompletionChecklistModal.css";

/**
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - onSubmit: ({ checklist: {primary:boolean[], optional:boolean[]}, meta: { minutesSpent:number|null }}) => Promise|void
 * - basePath: string[]   // e.g. ["missions", missionId]
 * - missionName?: string // optional override
 * - honestyNote?: string
 */
export default function CompletionChecklistModal({
  open,
  onClose,
  onSubmit,
  basePath,
  missionName: missionNameProp,
  honestyNote = "This is just for your personal progress tracking. It's private and not visible to other users — please be honest.",
}) {
  const [missionName, setMissionName] = useState(missionNameProp || "Mission");
  const [primaryList, setPrimaryList] = useState([]);
  const [optionalList, setOptionalList] = useState([]);

  const [primaryChecks, setPrimaryChecks] = useState([]);
  const [optionalChecks, setOptionalChecks] = useState([]);
  const [minutesSpent, setMinutesSpent] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch mission objectives on open
  useEffect(() => {
    if (!open) return;
    if (!Array.isArray(basePath) || basePath.length === 0) return;

    setLoading(true);
    const unsub = onSnapshot(
      doc(db, ...basePath),
      (snap) => {
        setLoading(false);
        if (!snap.exists()) return;
        const data = snap.data() || {};
        const prim = Array.isArray(data?.objectives?.primary)
          ? data.objectives.primary
          : [];
        const opt = Array.isArray(data?.objectives?.optional)
          ? data.objectives.optional
          : [];
        setMissionName(missionNameProp || data?.missionName || "Mission");
        setPrimaryList(prim);
        setOptionalList(opt);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, [open, basePath, missionNameProp]);

  // Reset UI state when opened or lists change
  useEffect(() => {
    if (!open) return;
    setPrimaryChecks(Array(primaryList.length).fill(false));
    setOptionalChecks(Array(optionalList.length).fill(false));
    setMinutesSpent("");
  }, [open, primaryList.length, optionalList.length]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const primaryDoneCount = useMemo(
    () => primaryChecks.filter(Boolean).length,
    [primaryChecks]
  );

  // Require at least one primary if there are primaries
  const canSubmit = primaryList.length === 0 || primaryDoneCount > 0;

  const togglePrimary = (i) =>
    setPrimaryChecks((prev) => {
      const next = [...prev];
      next[i] = !next[i];
      return next;
    });

  const toggleOptional = (i) =>
    setOptionalChecks((prev) => {
      const next = [...prev];
      next[i] = !next[i];
      return next;
    });

  const toggleAllPrimary = (checked) =>
    setPrimaryChecks(Array(primaryList.length).fill(!!checked));

  const toggleAllOptional = (checked) =>
    setOptionalChecks(Array(optionalList.length).fill(!!checked));

  const stop = (e) => e.stopPropagation();

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit?.({
        checklist: {
          primary: primaryChecks,
          optional: optionalChecks,
        },
        meta: {
          minutesSpent: minutesSpent ? Number(minutesSpent) : null,
        },
      });
      onClose?.();
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="ccm-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Complete ${missionName}`}
      onClick={onClose}
    >
      <div className="ccm-dialog" onClick={stop}>
        {/* Header */}
        <div className="ccm-header">
          <h3 className="ccm-title">Complete: {missionName}</h3>
          <button className="ccm-close" onClick={onClose} aria-label="Close" type="button">
            ✕
          </button>
        </div>

        {/* Honesty note */}
        {honestyNote && (
          <div className="ccm-note" role="status" aria-live="polite">
            {honestyNote}
          </div>
        )}

        {/* Body */}
        <div className="ccm-body">
          {loading && <div className="ccm-loading">Loading objectives…</div>}

          {/* Primary */}
          {!loading && primaryList.length > 0 && (
            <section className="ccm-section">
              <div className="ccm-section-head">
                <h4 className="ccm-h4">Primary objectives</h4>
                <label className="ccm-check ccm-check--all">
                  <input
                    type="checkbox"
                    checked={
                      primaryList.length > 0 &&
                      primaryDoneCount === primaryList.length
                    }
                    onChange={(e) => toggleAllPrimary(e.target.checked)}
                  />
                  <span>Mark all primary as completed</span>
                </label>
              </div>

              <ul className="ccm-list">
                {primaryList.map((text, i) => (
                  <li key={`p-${i}`} className="ccm-li">
                    <label className="ccm-check">
                      <input
                        type="checkbox"
                        checked={!!primaryChecks[i]}
                        onChange={() => togglePrimary(i)}
                      />
                      <span>{text}</span>
                    </label>
                  </li>
                ))}
              </ul>
              <div className="ccm-progress">
                Selected {primaryDoneCount}/{primaryList.length}
              </div>
            </section>
          )}

          {/* Optional */}
          {!loading && optionalList.length > 0 && (
            <section className="ccm-section">
              <div className="ccm-section-head">
                <h4 className="ccm-h4">Optional objectives</h4>
                <label className="ccm-check ccm-check--all">
                  <input
                    type="checkbox"
                    checked={optionalList.length > 0 && optionalChecks.every(Boolean)}
                    onChange={(e) => toggleAllOptional(e.target.checked)}
                  />
                  <span>Mark all optional as completed</span>
                </label>
              </div>

              <ul className="ccm-list">
                {optionalList.map((text, i) => (
                  <li key={`o-${i}`} className="ccm-li">
                    <label className="ccm-check">
                      <input
                        type="checkbox"
                        checked={!!optionalChecks[i]}
                        onChange={() => toggleOptional(i)}
                      />
                      <span>{text}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Duration only */}
          {!loading && (
            <div className="ccm-grid">
              <label className="ccm-field" style={{ gridColumn: "1 / -1" }}>
                <span>Duration (minutes)</span>
                <input
                  type="number"
                  min="0"
                  inputMode="numeric"
                  value={minutesSpent}
                  onChange={(e) => setMinutesSpent(e.target.value)}
                  placeholder="e.g. 25"
                />
              </label>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="ccm-footer">
          <button className="ccm-btn" type="button" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="ccm-btn ccm-btn--primary"
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting || loading}
            title={
              !canSubmit && !loading && primaryList.length > 0
                ? "Select at least one primary objective"
                : undefined
            }
          >
            {submitting ? "Submitting…" : "Submit completion"}
          </button>
        </div>
      </div>
    </div>
  );
}
